import { Direction } from '../BaseCar/BaseCar.types';
import { SEAT_GROUP_WHEELCHAIR } from '../BaseCar/constants';
import { ICar2027Info } from './BaseCar2027.types';
import { ENTRANCE, GROUP_DIRECTION, SEAT_SIDE } from './directions';

type ISeatOrder = Record<string, number>;

export {
  ENTRANCE,
  GROUP_DIRECTION,
  SEAT_SIDE,
  PHYSICAL_ZONE_KIND
} from './directions';

export const SEAT_IDS_2027 = ['A', 'B', 'C', 'D'];

/**
 * Grid order of the 4 seats (2 cols x 2 rows; 0=top-left 1=top-right
 * 2=bottom-left 3=bottom-right). Each row of groups uses a different order:
 *   - top-row groups:    A top-left, B bottom-left, C bottom-right, D top-right
 *   - bottom-row groups: A bottom-left, B top-left, C top-right, D bottom-right
 */
const SEAT_TOP_ORDER_2027: ISeatOrder = {
  A: 0,
  D: 1,
  B: 2,
  C: 3
};

const SEAT_BOTTOM_ORDER_2027: ISeatOrder = {
  B: 0,
  C: 1,
  A: 2,
  D: 3
};

export const SEAT_ORDER_2027: Record<string, ISeatOrder> = {
  [GROUP_DIRECTION.Top]: SEAT_TOP_ORDER_2027,
  [GROUP_DIRECTION.Bottom]: SEAT_BOTTOM_ORDER_2027
};

/** Seat back (border line): A/B on the left, C/D on the right */
export const SEAT_DIRECTION_2027: Record<string, Direction> = {
  A: SEAT_SIDE.Left,
  B: SEAT_SIDE.Left,
  C: SEAT_SIDE.Right,
  D: SEAT_SIDE.Right
};

/** Special-room capacity (business rule: 1 room per service, max 6 people) */
export const SPECIAL_ROOM_CAPACITY = 6;

/** Car 2 wheelchair-space groups (19A/20A), drawn with a wheelchair block */
export const WHEELCHAIR_SPACE_GROUP_IDS = ['19', '20'];

/** Seat ID for the wheelchair-space groups (19A/20A) */
export const WHEELCHAIR_SPACE_GROUP_SEAT_IDS = ['A'];

/**
 * Backend ID scheme for the 2027 train (physicalTrainId=3). The seat-group /
 * car / wheelchair IDs registered on the backend differ from the legacy model;
 * sending legacy IDs (`01`-`22`, car `1`-`4`, `wheelchair`) fails the
 * trainservice_arrangements foreign-key constraint. These helpers are the
 * single source of truth — both the request builder (initialCars2027) and the
 * render layer (BaseCar2027) must produce identical IDs so the arrangement
 * lookup by seatGroupId/seatId matches.
 *   - physicalCarId:  '11'/'12'/'13'/'14', held on each cars2027Info entry
 *   - seat group:     '27' + 2-digit group no (e.g. '2703'); AB-only groups get
 *                     a trailing 'AB' (e.g. '2701AB'), CD-only a trailing 'CD'
 *   - wheelchair:     group '27wheelchair', seat ids wheelchair_01/02 (unchanged)
 */
export const SEAT_GROUP_PREFIX_2027 = '27';

export const SEAT_GROUP_WHEELCHAIR_2027 = '27wheelchair';

/**
 * True for a wheelchair seat-group in either train model: legacy ('wheelchair')
 * or 2027 ('27wheelchair'). Wheelchair seats are listed separately from normal
 * seats everywhere they are displayed, so both ids must be recognised — matching
 * only the legacy id leaks 2027 wheelchair seats into the normal-seat list (and
 * drops the wheelchair line entirely).
 */
export const isWheelchairSeatGroup = (seatGroupId?: string) =>
  seatGroupId === SEAT_GROUP_WHEELCHAIR ||
  seatGroupId === SEAT_GROUP_WHEELCHAIR_2027;

/**
 * Seat-group id for the 2027 train. groupNo is the 1-based group number; the
 * 'AB'/'CD' suffix is added only for end groups that carry a single seat pair.
 */
export const seatGroupId2027 = (
  groupNo: number | string,
  options: { abOnly?: boolean; cdOnly?: boolean } = {}
): string => {
  const base = `${SEAT_GROUP_PREFIX_2027}${`${groupNo}`.padStart(2, '0')}`;
  if (options.abOnly) return `${base}AB`;
  if (options.cdOnly) return `${base}CD`;
  return base;
};

/**
 * Inverse of seatGroupId2027: strip the '27' prefix and any 'AB'/'CD' suffix to
 * recover the 2-digit group number (e.g. '2701AB' -> '01', '2703' -> '03').
 * Used to match user-entered seat numbers (1A, 22D) against 2027 arrangements
 * whose seatGroupId carries the backend prefix/suffix. Non-2027 ids fall back
 * to the raw 2-digit group number so legacy data still matches.
 */
export const seatGroupNo2027 = (seatGroupId = ''): string => {
  const match = seatGroupId.match(/^27(\d{2})(?:AB|CD)?$/);
  return match ? match[1] : seatGroupId;
};

/**
 * Display car number (1-based) for a 2027 backend physicalCarId: '11' -> '1',
 * '12' -> '2', '13' -> '3', '14' -> '4' (position in cars2027Info). Ids that
 * aren't 2027 (e.g. the old train's '1'..'5') are returned unchanged, so shared
 * carriage labels (seat.carriage.carriageN) keep resolving for both layouts.
 */
export const physicalCarNo2027 = (physicalCarId = ''): string => {
  const index = cars2027Info.findIndex(
    car => car.physicalCarId === physicalCarId
  );
  return index >= 0 ? `${index + 1}` : physicalCarId;
};

/**
 * New train layout (Spring 2027): cars 1-3 + car 4. The whole car is drawn
 * rotated 180°: seat numbers ascend from 1 at the left (嵯峨) rightward, even
 * groups on the top row and odd on the bottom; `count` is the seat-group count
 * per car (4 seats each). Rebuilt from the seat-map prototype.
 *   - Car 1: seats 1-22 + left open-deck/traction
 *   - Car 2: seats 1-20 (wheelchair space at 19/20) + right open-deck/conductor
 *   - Car 3: seats 1-22 + open-deck on the right end
 *   - Car 4: seats 1-16 + right driver/special room + left open-deck
 */
export const cars2027Info: ICar2027Info[] = [
  // 1号車: groups 1/2 (left end) have only A/B; entrance at the open-deck bottom-left
  {
    physicalCarId: '11',
    count: 22,
    leftZones: [
      { kind: 'tractionCar' },
      { kind: 'openDeck', entrance: ENTRANCE.BottomLeft }
    ],
    abOnlyGroupIds: ['1', '2']
  },
  // 2号車: 17-20 only A/B (19/20 wheelchair); entrance at the open-deck bottom-right
  {
    physicalCarId: '12',
    count: 20,
    rightZones: [
      { kind: 'conductorRoom' },
      { kind: 'openDeck', entrance: ENTRANCE.BottomRight }
    ],
    wheelchairSpaceGroupIds: WHEELCHAIR_SPACE_GROUP_IDS,
    abOnlyGroupIds: ['17', '18', '19', '20']
  },
  // 3号車: 21/22 (right end) have only C/D; entrance at the right open-deck bottom-right
  {
    physicalCarId: '13',
    count: 22,
    rightZones: [{ kind: 'openDeck', entrance: ENTRANCE.BottomRight }],
    cdOnlyGroupIds: ['21', '22']
  },
  // 4号車: groups 1/2 (left end) have only A/B (1A 1B 2A 2B); entrance at the open-deck bottom-left
  {
    isHead: true,
    physicalCarId: '14',
    count: 16,
    leftZones: [{ kind: 'openDeck', entrance: ENTRANCE.BottomLeft }],
    rightZones: [{ kind: 'specialRoom' }, { kind: 'driverRoom' }],
    abOnlyGroupIds: ['1', '2']
  }
];
